import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { printInOwnWindow } from '../utils/printWindow';
import { Card, CardHeader, CardTitle, CardContent, Button, Input, Select } from './ui';
import { api } from '../lib/api';
import type { BrochureBlockInput, BrochureDraftKind, BrochureGenerateInput, SavedBrochureSummary } from '../types';
import {
  type BlockKind, type EditorBlock, type EditorSection, type CarriedInput, type EditorState,
  lines, newBlock, newSection, statCardsToText, toInput, fromInput, FLYER_SLOTS, FLYER_SLOT_INFO, flyerPickOptions,
} from '../utils/brochureEditorModel';

/**
 * The brochure / Offering Memorandum editor: A FORM PLUS A LIVE PREVIEW OF THE REAL PAGE.
 *
 * ⛔ NOT A DRAG-AND-DROP CANVAS, and that was the design decision, not a shortcut. Every one of
 * his brochures is the same small block vocabulary in the same order, so the content is
 * STRUCTURED DATA and the editor is a form. A canvas would let him move a box two points left
 * and would not make a single brochure easier to produce.
 *
 * ⭐⭐ THE PREVIEW IS RENDERED BY THE SERVER, NOT BY THIS COMPONENT. Porting the renderer here
 * would give one document two renderers, which is the defect this build has already paid for
 * twice - the listing proposal's .docx carried a letterhead its print HTML did not, and then
 * its .docx stacked the signature blocks its HTML flexed side by side. Every keystroke here
 * (debounced) posts to the SAME endpoint the download uses, so what he previews is literally
 * what he gets. Preview posts carry NO deal id, so previewing never files.
 *
 * ⚠️ AND IT MEASURES OVERFLOW. A brochure page is a fixed box and over-long content is CLIPPED -
 * that is what will print, and it is silent. The preview measures each page's content against
 * the page and names the ones that overflow, because a clip nobody is told about is the same
 * shape as the contract blank that was too narrow to draw into.
 */

export interface BrochurePrefill {
  addressLine?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  county?: string | null;
  zoning?: string | null;
  apn?: string | null;
  apnSource?: string | null;
  coverHeadline?: string | null;
  coverSubhead?: string | null;
  /**
   * ⭐⭐ THE CHOICES HE STATES ABOUT THE DOCUMENT (the 99th). The brochure door in the AI rail
   * declared no parameters at all until now, so "make a LEASE FLYER with the manchester look"
   * opened a for-sale brochure in the Apex theme and said nothing about it.
   *
   * ⛔ Each is UNDEFINED unless he said it, and an undefined one keeps the editor's own default -
   *   a prefill carrying only a theme must not reset the format to something nobody chose.
   */
  format?: 'brochure' | 'flyer' | null;
  dealKind?: 'sale' | 'lease' | null;
  theme?: 'apex' | 'manchester' | 'classic' | null;
  tagline?: string | null;
  suggestedFacts?: { label: string; value: string }[];
  /** The property's own photographs. Measured: 2,537 of 3,570 properties have exactly ONE, so
   *  the hero goes on the cover and a grid page is only offered when there are two or more. */
  photos?: {
    hero?: { src: string; alt?: string | null } | null;
    sitePlan?: { src: string; alt?: string | null } | null;
    extras?: { src: string; alt?: string | null }[];
    /** The hero is a Google STREET VIEW capture, watermarked in the pixels. Reported, not refused. */
    heroIsStreetView?: boolean;
  } | null;
  /** Trade-area rings the app already computes, ready to drop in. */
  demographics?: {
    block: BrochureBlockInput;
    sourceNote: string;
    centre: { lat: number; lng: number };
  } | null;
}

interface Generated {
  html: string;
  filename: string;
  title: string;
  document_kind: 'Brochure' | 'Offering Memorandum' | 'Flyer';
  page_count: number;
  page_size: string;
  missing: string[];
  filed: boolean;
}

const KIND_HELP: Record<BlockKind, string> = {
  facts: 'One "Label: Value" per line.',
  bullets: 'One bullet per line.',
  prose: 'Paragraphs, separated by a blank line.',
  groups: 'One "Label: Text" per line - a red label over a grey line. Add "columns: 3" as the first line for a grid of cells.',
  callout: 'First line is the big figure (optional), the rest is the body.',
  statBand: 'First line the small label, second the big figure, then "Label: Value" per line.',
  statCards: 'A "## Card title" line, then "value | label" lines. Repeat per card.',
  statRow: 'One "value | label" per line - equal boxes in a row. A line starting with "note:" prints under them.',
  photos: 'Use the photo picker below. One caption per line, in photo order; a single line for several photos captions the grid. "height: 300" or "columns: 4" on their own lines size the grid.',
  table: 'First line the columns, separated by |. Then one row per line. A line starting with = is the totals row.',
};

/**
 * What a deal kind decides: the page size default, and which highlights title AI drafts under.
 * ⭐ NOT the closing bands - measured 2026-09-08, four of five lease brochures carry the house
 * wording, so the bands are their own choice below.
 */
const DEAL_KINDS = [
  { value: 'sale', label: 'Sale - 768 x 576' },
  { value: 'lease', label: 'Lease - 792 x 612' },
];

/**
 * ⭐ The FORMAT. His ask, 2026-09-08: a two-page flyer like the 695 one, that "can just work with
 * other styles of brochures/OMs as well". It is the same form projected onto two pages - the
 * cover, the first fact card, the highlights, the drive times, the demographics and the ideal
 * users - so switching it loses nothing he typed.
 */
const FORMATS = [
  { value: 'brochure', label: 'Brochure / OM - one page per section' },
  { value: 'flyer', label: 'Flyer - 2 pages (front: photo + facts; back: map + demographics)' },
];

/**
 * ⭐ The LOOK. His rulings 2026-09-09: first "the manchester look where it has the red and our logos
 * in nice places"; then, on seeing it on a brochure, "go with option A, build the cowork brochure
 * look" - so the default is the look of his OWN September 2026 brochures (2255 E Florida, 695 S
 * Williams), Manchester stays as the investment-analysis chrome, and the Canva chrome stays because
 * the San Jacinto sale standard is measured in it.
 */
const LOOKS = [
  { value: 'apex', label: 'Apex brochure - your Cowork look: Oswald, crimson, corner ribbon (2255 / 695)' },
  { value: 'manchester', label: 'Manchester - the investment-analysis chrome (red masthead, Calibri)' },
  { value: 'classic', label: 'Classic - the Canva house chrome (608 & 614 N San Jacinto)' },
];

/** The closing bands, a per-document choice with a house default. `leasing` is Cornell Plaza's. */
const BAND_SETS = [
  { value: 'house', label: 'House - "building better communities", "scan the QR code below"' },
  { value: 'leasing', label: 'Leasing - "working with you", "scan to learn more" (Cornell Plaza)' },
];

/** The contact page's lead line - the standing one, or the tour line both 2026 specimens use. */
const CONTACT_LEADS = [
  { value: '', label: 'FOR FURTHER INQUIRIES, PLEASE CONTACT' },
  { value: 'FOR FURTHER INQUIRIES OR TO SCHEDULE A TOUR', label: 'FOR FURTHER INQUIRIES OR TO SCHEDULE A TOUR' },
];

/** Measured from his own covers - see BrochureCoverStyle in brochureBuilderService. */
const COVER_STYLES = [
  { value: 'card', label: 'White card (San Jacinto)' },
  { value: 'overlay', label: 'Type on photo (Beaumont)' },
  { value: 'band', label: 'Red band across the foot' },
];

const COLUMNS = [
  { value: '', label: 'auto column' },
  { value: '1', label: 'left column' },
  { value: '2', label: 'right column' },
];

const KINDS: { value: BlockKind; label: string }[] = [
  { value: 'facts', label: 'Fact card (Label: Value rows)' },
  { value: 'bullets', label: 'Bullet card' },
  { value: 'prose', label: 'Prose card' },
  { value: 'groups', label: 'Labelled groups' },
  { value: 'callout', label: 'Red-outlined callout' },
  { value: 'statBand', label: 'Solid red stat band' },
  { value: 'statCards', label: 'Stat cards (demographics)' },
  { value: 'statRow', label: 'Stat row (equal figures)' },
  { value: 'table', label: 'Table (suites, rent roll)' },
  { value: 'photos', label: 'Photo grid' },
];


/**
 * ⭐ The highlights card's LABEL changes with the deal kind, because his documents do: six lease
 * brochures say PROPERTY HIGHLIGHTS and two sale brochures say INVESTMENT HIGHLIGHTS. The
 * server decides the actual title; this is only what the checkbox is called.
 */
const DRAFT_OPTIONS: { value: BrochureDraftKind; label: string }[] = [
  { value: 'highlights', label: 'Highlights' },
  { value: 'idealUses', label: 'Ideal uses' },
  { value: 'location', label: 'Location narrative' },
  { value: 'buyerPool', label: 'Buyer pool' },
  { value: 'tagline', label: 'Cover tagline' },
];

/**
 * ⭐ THE EDITOR KNOWS THE TEXT FORMAT, SO IT SHOULD CATCH THE MISTAKE - NOT THE SERVER.
 *
 * A fact card and a groups card are "Label: Value" per line. Typing a sentence into one makes
 * the whole sentence the label with nothing in the value, and the builder then refuses with a
 * message that quotes the sentence back as if it were a label. Joseph hit exactly that on his
 * first brochure and reasonably read it as "I must make an Executive Summary first".
 *
 * This names the offending line while he is still typing, and says what to do about it.
 */
const pairFormatProblem = (kind: BlockKind, body: string): string | null => {
  if (kind !== 'facts' && kind !== 'groups') return null;
  const bad = lines(body)
    .map((l, i) => ({ l, n: i + 1 }))
    .filter(({ l }) => !l.includes(':'));
  if (!bad.length) return null;
  const which = bad.length === 1 ? `Line ${bad[0].n} has` : `Lines ${bad.map((b) => b.n).join(', ')} have`;
  return (
    `${which} no colon, so there is nothing to put in the value column. ` +
    `This card is "Label: Value" per line - like "Price: $279,000". ` +
    `For a sentence, switch this block to a Bullet card or a Prose card.`
  );
};

const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function BrochureGeneratorDialog({
  open, onClose, prefill, dealId, propertyId, masterPropertyId, initialSavedId, onFiled,
}: {
  open: boolean;
  onClose: () => void;
  prefill?: BrochurePrefill;
  dealId?: string;
  /** The analyzer property (the property page) or the Property Database record (the data hub):
   *  the surface a save is keyed to and whose saved brochures the editor offers. */
  propertyId?: string;
  masterPropertyId?: string;
  /** Open straight onto a saved brochure - the Document Generator's "Open in editor". */
  initialSavedId?: string | null;
  onFiled?: () => void;
}) {
  const [addressLine, setAddressLine] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('CA');
  const [zip, setZip] = useState('');
  const [county, setCounty] = useState('');
  const [dealKind, setDealKind] = useState<'sale' | 'lease'>('sale');
  const [format, setFormat] = useState<'brochure' | 'flyer'>('brochure');
  const [theme, setTheme] = useState<'apex' | 'manchester' | 'classic'>('apex');
  const [flyerSummary, setFlyerSummary] = useState('');
  /** One caption per line for the flyer's two back-page photographs (the first two grid photos). */
  const [flyerCaptions, setFlyerCaptions] = useState('');
  /** FL2 - the cards he named for the flyer's five slots; an unset slot is picked by title. */
  const [flyerPicks, setFlyerPicks] = useState<EditorState['flyerPicks']>({});
  const [bandSet, setBandSet] = useState<'house' | 'leasing'>('house');
  const [coverStyle, setCoverStyle] = useState<'card' | 'overlay' | 'band'>('card');
  const [tagline, setTagline] = useState('');
  const [headline, setHeadline] = useState('');
  const [subhead, setSubhead] = useState('');
  const [emphasis, setEmphasis] = useState('');
  // The newer covers (2255, 695): a stat strip, a byline, titled brokers, a running footer.
  const [coverStats, setCoverStats] = useState('');
  const [byline, setByline] = useState('');
  const [brokerTitles, setBrokerTitles] = useState(false);
  const [pageFooter, setPageFooter] = useState(false);
  const [footerTag, setFooterTag] = useState('');
  const [omitBack, setOmitBack] = useState(false);
  const [contactLead, setContactLead] = useState('');
  const [contactNote, setContactNote] = useState('');
  const [disclaimerText, setDisclaimerText] = useState('');
  const [backBand, setBackBand] = useState('');
  const [includeRobert, setIncludeRobert] = useState(true);
  const [includeJoseph, setIncludeJoseph] = useState(true);
  const [sections, setSections] = useState<EditorSection[]>([]);
  const [coverPhoto, setCoverPhoto] = useState<string | null>(null);
  const [backPhoto, setBackPhoto] = useState<string | null>(null);
  const [gridPhotos, setGridPhotos] = useState<{ src: string; alt?: string }[]>([]);
  const [planPhoto, setPlanPhoto] = useState<string | null>(null);
  const [qrSrc, setQrSrc] = useState<string | null>(null);

  // --- AI drafting -------------------------------------------------------------------------
  // ⭐ Defaults are the three Joseph named as ready to build now. `buyerPool` appears in ONE of
  // his eighteen brochures, so it is offered and never pre-ticked; `tagline` is a cover
  // decision he usually makes himself.
  const [draftKinds, setDraftKinds] = useState<BrochureDraftKind[]>(['highlights', 'idealUses', 'location']);
  const [guidance, setGuidance] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [dropped, setDropped] = useState<
    { kind: BrochureDraftKind; line: string; claims: { claim: string; kind: string }[] }[]
  >([]);
  const [draftNote, setDraftNote] = useState<string | null>(null);
  /**
   * Uses the rule book could not state on this document, and why. ⭐ Same contract as `dropped`:
   * REPORTED, NEVER SILENT. A withheld use is usually one Planning call from being printable, so
   * it is a to-do for him rather than an apology from the software.
   */
  const [withheld, setWithheld] = useState<{ use: string; reason: string }[]>([]);
  /** Why the zoning page is absent when one was asked for. Reported, never silent. */
  const [zoningRefusal, setZoningRefusal] = useState<string | null>(null);
  const [wantZoningPage, setWantZoningPage] = useState(false);

  const [preview, setPreview] = useState<Generated | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [overflowing, setOverflowing] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [filed, setFiled] = useState<string | null>(null);
  const frame = useRef<HTMLIFrameElement | null>(null);
  /** The saved brochure the editor currently holds, if it was loaded rather than seeded (LB4). */
  const loadedRef = useRef<string | null>(null);
  const [carried, setCarried] = useState<CarriedInput>({});

  useEffect(() => {
    if (!open) return;
    // A loaded brochure (LB4) is the state now; a prefill that re-arrives must not wipe it.
    if (loadedRef.current) return;
    setAddressLine(prefill?.addressLine ?? '');
    setCity(prefill?.city ?? '');
    setState(prefill?.state ?? 'CA');
    setZip(prefill?.zip ?? '');
    setCounty(prefill?.county ?? '');
    setHeadline(prefill?.coverHeadline ?? '');
    setSubhead(prefill?.coverSubhead ?? '');
    setFiled(null);

    /**
     * ⭐⭐ THE 99TH - the choices he stated in chat, seeded onto the controls.
     *
     * ⛔ `??` AND NOT `?? ''` HERE, deliberately, and it is the whole difference: these three are
     *   CHOICES with real defaults, not text fields. Blanking an unstated one would open the
     *   editor on a format or a look nobody picked, which is worse than ignoring him - so an
     *   absent value keeps the default the editor already holds.
     */
    if (prefill?.format) setFormat(prefill.format);
    if (prefill?.dealKind) setDealKind(prefill.dealKind);
    if (prefill?.theme) setTheme(prefill.theme);
    setTagline(prefill?.tagline ?? '');

    // ⭐ The record's own photographs, straight onto the pages they belong on. His San Jacinto
    // brochure uses the SAME aerial on the cover and the back cover, so the hero does both.
    setCoverPhoto(prefill?.photos?.hero?.src ?? null);
    setBackPhoto(prefill?.photos?.hero?.src ?? null);
    // ⚠️ Only EXTRA exteriors can fill a grid - the hero is already the cover and the site plan
    // gets its own block. With one photo (2,537 of 3,570 properties) this is empty, and the
    // grid page is simply not offered rather than being drawn with one lonely picture.
    setGridPhotos((prefill?.photos?.extras ?? []).map((p) => ({ src: p.src, alt: p.alt ?? undefined })));

    const built: EditorSection[] = [];

    const summary = newSection('EXECUTIVE SUMMARY');
    if (prefill?.suggestedFacts?.length) {
      summary.blocks[0] = {
        ...newBlock('facts'),
        title: 'PROPERTY SUMMARY',
        body: prefill.suggestedFacts.map((f) => `${f.label} ${f.value}`).join('\n'),
      };
    }
    built.push(summary);

    if (prefill?.photos?.sitePlan?.src) {
      const plan = newSection('SITE PLAN');
      plan.omitDisclaimer = true;
      plan.blocks = [{ ...newBlock('photos'), title: 'SITE PLAN', body: '' }];
      built.push(plan);
      setPlanPhoto(prefill.photos.sitePlan.src);
    } else {
      setPlanPhoto(null);
    }

    if (prefill?.demographics) {
      const demo = newSection('AREA DEMOGRAPHICS');
      demo.subtitle = 'Population, income and workforce within 1, 3, and 5 miles of the site';
      demo.sourceNote = prefill.demographics.sourceNote;
      // Carried as a ready-made block, not as text - these are FACTS the app computed, and
      // round-tripping them through the "value | label" editor format would invite a typo.
      demo.blocks = [{ ...newBlock('statCards'), title: '', body: statCardsToText(prefill.demographics.block) }];
      built.push(demo);
    }

    setSections(built);
  }, [open, prefill]);

  const editorState: EditorState = useMemo(() => ({
    addressLine, city, state, zip, county, dealKind, format, theme, flyerSummary, flyerCaptions, flyerPicks, bandSet,
    coverStyle, tagline, headline, subhead, emphasis, coverStats, byline, brokerTitles, pageFooter, footerTag,
    omitBack, contactLead, contactNote, disclaimerText, backBand, includeRobert, includeJoseph,
    sections, coverPhoto, backPhoto, gridPhotos, planPhoto, qrSrc, carried,
  }), [addressLine, city, state, zip, county, dealKind, format, theme, flyerSummary, flyerCaptions, flyerPicks, bandSet,
      coverStyle, tagline, headline, subhead, emphasis, coverStats, byline, brokerTitles, pageFooter, footerTag,
      omitBack, contactLead, contactNote, disclaimerText, backBand, sections, coverPhoto, backPhoto,
      gridPhotos, planPhoto, qrSrc, includeRobert, includeJoseph, carried]);
  /** The parsed payload the server receives - one forward mapping, the model's (`toInput`). */
  const input: BrochureGenerateInput | null = useMemo(() => toInput(editorState), [editorState]);

  // ---- LB4: the saved brochures of this surface, and loading one ----
  const savedKey = useMemo(
    () => (dealId ? { dealId } : propertyId ? { propertyId } : masterPropertyId ? { masterPropertyId } : null),
    [dealId, propertyId, masterPropertyId]
  );
  const [savedList, setSavedList] = useState<SavedBrochureSummary[]>([]);
  const [savedPick, setSavedPick] = useState('');
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [loadedNote, setLoadedNote] = useState<string | null>(null);

  /** Every control takes the loaded value - the same setters the form's own inputs use. */
  const applyState = (st: EditorState) => {
    setAddressLine(st.addressLine); setCity(st.city); setState(st.state); setZip(st.zip); setCounty(st.county);
    setDealKind(st.dealKind); setFormat(st.format); setTheme(st.theme);
    setFlyerSummary(st.flyerSummary); setFlyerCaptions(st.flyerCaptions); setFlyerPicks(st.flyerPicks); setBandSet(st.bandSet);
    setCoverStyle(st.coverStyle); setTagline(st.tagline); setHeadline(st.headline); setSubhead(st.subhead); setEmphasis(st.emphasis);
    setCoverStats(st.coverStats); setByline(st.byline); setBrokerTitles(st.brokerTitles);
    setPageFooter(st.pageFooter); setFooterTag(st.footerTag); setOmitBack(st.omitBack);
    setContactLead(st.contactLead); setContactNote(st.contactNote); setDisclaimerText(st.disclaimerText);
    setBackBand(st.backBand); setIncludeRobert(st.includeRobert); setIncludeJoseph(st.includeJoseph);
    setSections(st.sections); setCoverPhoto(st.coverPhoto); setBackPhoto(st.backPhoto);
    setGridPhotos(st.gridPhotos); setPlanPhoto(st.planPhoto); setQrSrc(st.qrSrc); setCarried(st.carried);
  };

  const loadSaved = async (id: string) => {
    setLoadingSaved(true);
    try {
      const res = await api.getSavedBrochure(id);
      const { state: st, notes } = fromInput(res.saved.input);
      loadedRef.current = id;
      applyState(st);
      setSavedPick(id);
      setFiled(null);
      const when = new Date(res.saved.createdAt).toLocaleString();
      setLoadedNote(
        `Loaded "${res.saved.title}" (saved ${when})` +
        (notes.length ? `. Kept as saved, not editable here: ${notes.join('; ')}.` : '.')
      );
    } catch (err) {
      setLoadedNote(err instanceof Error ? err.message : 'Could not load the saved brochure');
    } finally { setLoadingSaved(false); }
  };

  useEffect(() => {
    if (!open) {
      loadedRef.current = null;
      setSavedList([]); setSavedPick(''); setLoadedNote(null); setCarried({});
      return;
    }
    let cancelled = false;
    if (savedKey) {
      api.listSavedBrochures(savedKey)
        .then((r) => { if (!cancelled) setSavedList(r.saved); })
        .catch(() => { if (!cancelled) setSavedList([]); });
    }
    if (initialSavedId) loadSaved(initialSavedId);
    return () => { cancelled = true; };
  }, [open, savedKey, initialSavedId]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * The zoning code THIS DOCUMENT states, read exactly as the server reads it.
   *
   * ⚠️ IT READS `input`, NOT THE EDITOR STATE. `input` is the parsed payload the server actually
   * receives, so both sides look at the same rows through the same rule. Deriving it from the
   * raw textareas instead would let the checkbox offer a page the server then cannot build - two
   * implementations of one question, which is the silent drift this build keeps paying for.
   *
   * ⚠️ KEEP IN STEP WITH `zoningCodeFromInput` in `backend/src/services/brochureCopyService.ts`.
   */
  const zoningCode = useMemo(() => {
    for (const section of input?.sections ?? []) {
      for (const block of section.blocks) {
        if (block.kind !== 'facts') continue;
        const row = block.rows.find((r) => /^zoning\b/i.test(r.label.replace(/[:\s]+$/, '')));
        if (row?.value?.trim()) return row.value.trim();
      }
    }
    return null;
  }, [input]);

  // Debounced live preview, off the SAME endpoint the download uses, never with a deal id.
  useEffect(() => {
    if (!open || !input) { setPreview(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.generateBrochure(input);
        if (!cancelled) { setPreview(res.brochure); setPreviewError(null); }
      } catch (err) {
        if (!cancelled) setPreviewError(err instanceof Error ? err.message : 'Could not build the preview');
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, input]);

  // Write the server's HTML into the frame, then MEASURE which pages overflow their fixed box.
  const paint = useCallback(() => {
    const doc = frame.current?.contentDocument;
    if (!doc || !preview) return;
    doc.open();
    doc.write(preview.html);
    doc.close();
    const over: number[] = [];
    doc.querySelectorAll<HTMLElement>('.page').forEach((page) => {
      const n = Number(page.dataset.page ?? 0);
      // ⭐ Every measurable box on the page, not only `.content`: the flyer's red panel and its
      // back-page body are fixed boxes too, and the first flyer render clipped ten ideal-user
      // chips with this check silent because it only knew the brochure's box. Found by looking.
      const boxes = [...page.querySelectorAll<HTMLElement>('[data-content]')];
      if (boxes.some((box) => box.scrollHeight > box.clientHeight + 1)) over.push(n);
    });
    setOverflowing(over);
  }, [preview]);

  useEffect(() => { paint(); }, [paint]);

  if (!open) return null;

  const patchSection = (id: number, patch: Partial<EditorSection>) =>
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const patchBlock = (sid: number, bid: number, patch: Partial<EditorBlock>) =>
    setSections((prev) => prev.map((s) => (s.id !== sid ? s
      : { ...s, blocks: s.blocks.map((b) => (b.id === bid ? { ...b, ...patch } : b)) })));

  /**
   * Draft the persuasion, then place it where his own brochures put it.
   *
   * ⭐⭐ THE PLACEMENT IS MEASURED, NOT GUESSED. Both specimens put the highlights card and the
   * IDEAL USES card on the EXECUTIVE SUMMARY page, beside the property summary - San Jacinto
   * (PROPERTY SUMMARY / IDEAL USES / INVESTMENT HIGHLIGHTS) and Cornell Plaza (PROPERTY
   * OVERVIEW / PROPERTY HIGHLIGHTS). The location prose is its own page, which Cornell titles
   * PROPERTY DESCRIPTION. So highlights and uses land on the first page, and the narrative
   * gets that page, created only if it is not already there.
   *
   * ⚠️ A draft never overwrites something he wrote. It appends a new block, or fills a block of
   * the right kind that is still empty.
   */
  const draft = async () => {
    // ⚠️ The zoning page counts as work. This guard used to demand a draft kind, which left the
    // button enabled for a zoning-only run and the handler silently returning - the button said
    // yes and nothing happened. Found by pressing it, not by asserting `disabled`.
    const wantsZoning = wantZoningPage && !!zoningCode;
    if (!input || (!draftKinds.length && !wantsZoning)) return;
    setDrafting(true);
    setDraftNote(null);
    try {
      const res = await api.draftBrochureCopy(
        input, draftKinds, guidance.trim() || null, wantsZoning
      );
      setDropped(res.dropped);
      setZoningRefusal(res.zoningRefusal ?? null);

      const bodyKinds = res.drafts.filter((d) => d.blockKind !== 'tagline');
      const line = res.drafts.find((d) => d.blockKind === 'tagline');
      if (line) setTagline(line.body);

      if (bodyKinds.length) {
        setSections((prev) => {
          const next = [...prev];
          if (!next.length) next.push(newSection('EXECUTIVE SUMMARY'));

          for (const d of bodyKinds) {
            const wantsOwnPage = d.kind === 'location' || d.kind === 'buyerPool';
            let idx = 0;
            if (wantsOwnPage) {
              const found = next.findIndex((sec) => /PROPERTY DESCRIPTION|LOCATION/i.test(sec.title));
              if (found >= 0) idx = found;
              else { next.push(newSection('PROPERTY DESCRIPTION')); idx = next.length - 1; }
            }
            const target = next[idx];
            const blockKind = d.blockKind as BlockKind;
            // Reuse an empty block of the right kind before adding one, so a freshly created
            // page does not keep the blank card the editor seeds it with.
            const empty = target.blocks.find(
              (b) => b.kind === blockKind && !b.title.trim() && !b.body.trim()
            );
            const seeded = target.blocks.find((b) => !b.title.trim() && !b.body.trim());
            const slot = empty ?? seeded;
            if (slot) {
              next[idx] = {
                ...target,
                blocks: target.blocks.map((b) =>
                  b.id === slot.id ? { ...b, kind: blockKind, title: d.title, body: d.body } : b
                ),
              };
            } else {
              next[idx] = {
                ...target,
                blocks: [...target.blocks, { ...newBlock(blockKind), title: d.title, body: d.body }],
              };
            }
          }
          return next;
        });
      }

      /**
       * ⛔ ZS3 - THE ZONING PAGE IS PLACED, NOT DRAFTED. It arrives assembled from the signed
       * rule book with a citation on every line and no model anywhere in its path, because a
       * permitted-use list is a statement about municipal code on a document carrying his DRE
       * number. It gets its OWN page, the way his hand-made San Jacinto one does.
       *
       * ⚠️ It never overwrites a zoning page he wrote himself - the same rule the drafts follow.
       * His own researched page is the better source; this is for the ones he has not typed.
       */
      if (res.zoning) {
        const z = res.zoning;
        setSections((prev) => {
          const found = prev.findIndex((sec) => /WHAT CAN BE BUILT|PERMITTED/i.test(sec.title));
          if (found >= 0 && prev[found].blocks.some((b) => b.body.trim())) return prev;

          const block: EditorBlock = {
            ...newBlock('groups'),
            title: 'PERMITTED BY RIGHT & CONDITIONAL USES',
            // ⭐ USE AND PERMISSION ONLY. The governing document is cited once in the source
            // note below - 88 of the 91 zones with a printable rule have exactly one - and
            // `section` is the research trail (94 chars average), which is provenance, not copy.
            body: z.lines.map((l) => `${l.use}: ${l.permission}`).join('\n'),
            full: true,
          };
          const page: EditorSection = {
            ...newSection('WHAT CAN BE BUILT HERE'),
            subtitle: `${z.city} ${z.code}`,
            sourceNote: z.sourceNote,
            blocks: [block],
          };
          if (found >= 0) {
            const next = [...prev];
            next[found] = { ...page, id: prev[found].id, title: prev[found].title };
            return next;
          }
          return [...prev, page];
        });
        setWithheld(z.withheld);
      } else {
        setWithheld([]);
      }

      /**
       * ⚠️ "Drafted 0 sections" reads as a FAILURE, and on a zoning-only run zero is the answer
       * he chose. The two halves are reported independently, and each is only mentioned when it
       * was actually asked for.
       */
      const kept = res.drafts.length;
      const zLines = res.zoning?.lines.length ?? 0;
      const said: string[] = [];
      if (draftKinds.length) {
        said.push(kept
          ? `Drafted ${kept} section${kept === 1 ? '' : 's'}`
          : 'Nothing survived the fact check - every drafted line asserted something the record does not carry');
      }
      if (zLines) {
        said.push(`built the zoning page from ${zLines} signed rule${zLines === 1 ? '' : 's'}`);
      }
      setDraftNote(
        said.length
          ? said.join(', and ') + '. Read every line before you send it.'
          : 'Nothing came back. See below.'
      );
    } catch (err) {
      setDraftNote(err instanceof Error ? err.message : 'Could not draft the copy');
    } finally {
      setDrafting(false);
    }
  };

  const save = async () => {
    if (!input) return;
    setBusy(true);
    try {
      const res = await api.generateBrochure(input, dealId, { propertyId, masterPropertyId });
      setPreview(res.brochure);
      const b = res.brochure;
      const said: string[] = [];
      if (b.filed) said.push('Filed on this deal - it is in the Deal Room documents list.');
      if (b.saved_id) said.push(`Saved as "${b.title}" - it is under Saved brochures and can be loaded again.`);
      else if (b.save_error) said.push(`Not saved for reloading: ${b.save_error}`);
      setFiled(said.join(' ') || null);
      if (b.saved_id && savedKey) {
        api.listSavedBrochures(savedKey).then((r) => setSavedList(r.saved)).catch(() => { /* the list is a convenience */ });
      }
      if (b.filed) onFiled?.();
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'Failed to generate the brochure');
    } finally { setBusy(false); }
  };

  // MOB1 (the 126th): the print window carries its way back - on his phone it had none.
  const print = () => {
    if (preview) printInOwnWindow(preview.html);
  };

  const download = () => {
    if (!preview) return;
    const blob = new Blob([preview.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = preview.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const pickPhoto = async (e: React.ChangeEvent<HTMLInputElement>, set: (v: string) => void) => {
    const f = e.target.files?.[0];
    if (f) set(await readAsDataUrl(f));
  };
  const pickGrid = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = [...(e.target.files ?? [])];
    setGridPhotos(await Promise.all(files.map(async (f) => ({ src: await readAsDataUrl(f), alt: f.name }))));
  };

  const ta = 'w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm font-mono';
  const label = 'block text-xs font-medium text-gray-700 mb-1';

  return (
    /* z-[1200], NOT z-50: Leaflet gives its own panes z-index 400-650, and at z-50 the property
       map draws straight THROUGH a fixed overlay. */
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1200] p-4">
      {/* A DEFINITE height (h-, not max-h-), and min-h-0 down the chain: with only a maximum the
          grid's h-full resolved to its CONTENT height (1,987px measured), so the column marked
          overflow-y-auto was as tall as its content and nothing scrolled - "there's no way to
          scroll down so I can finish it" (MM1). Now the columns are the scroll containers. */}
      <Card className="max-w-[95vw] w-full h-[92vh] overflow-hidden flex flex-col">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              {preview ? preview.document_kind : 'Brochure'}
              {preview && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  {preview.page_count} pages - {preview.page_size === 'four-three' ? '768 x 576' : '792 x 612'}
                </span>
              )}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>
        </CardHeader>

        <CardContent className="flex-1 min-h-0 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 lg:grid-rows-[minmax(0,1fr)] gap-4 h-full min-h-0">
            {/* ---- the form ---- */}
            <div className="min-h-0 overflow-y-auto pr-2 space-y-4">
              {previewError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                  {previewError}
                </div>
              )}

              {/* LB4 - the brochures saved on this deal / property / record, and the one loaded */}
              {(savedList.length > 0 || loadedNote) && (
                <div className="p-2 rounded-lg border border-gray-200 bg-gray-50 space-y-1" data-testid="saved-brochures">
                  <label className={label}>Saved brochures</label>
                  {savedList.length > 0 && (
                    <div className="flex gap-2 items-center">
                      <Select
                        value={savedPick}
                        onChange={(e) => setSavedPick(e.target.value)}
                        options={[
                          { value: '', label: `${savedList.length} saved - pick one to load` },
                          ...savedList.map((sv) => ({
                            value: sv.id,
                            label: `${sv.title} - ${sv.documentKind}${sv.format === 'flyer' ? ' (flyer)' : ''} - ${new Date(sv.createdAt).toLocaleString()}`,
                          })),
                        ]}
                      />
                      <Button variant="outline" size="sm" onClick={() => savedPick && loadSaved(savedPick)} disabled={!savedPick || loadingSaved}>
                        {loadingSaved ? 'Loading...' : 'Load'}
                      </Button>
                    </div>
                  )}
                  {loadedNote && <p className="text-xs text-gray-600" data-testid="loaded-note">{loadedNote}</p>}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <label className={label}>Street address</label>
                  <Input value={addressLine} onChange={(e) => setAddressLine(e.target.value)} placeholder="608 & 614 N San Jacinto St" />
                </div>
                <div><label className={label}>City</label><Input value={city} onChange={(e) => setCity(e.target.value)} /></div>
                <div><label className={label}>State</label><Input value={state} onChange={(e) => setState(e.target.value)} /></div>
                <div><label className={label}>ZIP</label><Input value={zip} onChange={(e) => setZip(e.target.value)} /></div>
                <div><label className={label}>County</label><Input value={county} onChange={(e) => setCounty(e.target.value)} /></div>
                <div className="col-span-2">
                  <label className={label}>Format</label>
                  <Select
                    value={format}
                    onChange={(e) => setFormat(e.target.value as 'brochure' | 'flyer')}
                    options={FORMATS}
                  />
                </div>
                <div className="col-span-2">
                  <label className={label}>Look</label>
                  <Select
                    value={theme}
                    onChange={(e) => setTheme(e.target.value as 'apex' | 'manchester' | 'classic')}
                    options={LOOKS}
                  />
                </div>
                {format === 'flyer' && (
                  <>
                    <div className="col-span-2">
                      <label className={label}>Flyer summary line (under the address on the front)</label>
                      <Input value={flyerSummary} onChange={(e) => setFlyerSummary(e.target.value)} placeholder="±7,500 SF single-tenant building on a ±0.31 AC gated corner lot · ..." />
                    </div>
                    <div className="col-span-2">
                      <label className={label}>Flyer back-page captions - one per line, for the first two photo-grid pictures (map, then site)</label>
                      <textarea className={ta} rows={2} value={flyerCaptions} onChange={(e) => setFlyerCaptions(e.target.value)} aria-label="Flyer captions" placeholder={'Locations approximate. Imagery © Esri.\nParcel per Riverside County GIS · not a survey'} />
                    </div>
                    {/* FL2 - which cards fill the flyer. "Automatic" is the title guess; a named card is used as named,
                        and a name that no longer resolves is reported by the builder, never replaced by the guess. */}
                    <div className="col-span-2 space-y-1" data-testid="flyer-picks">
                      <label className={label}>Flyer cards - which card fills each slot</label>
                      {FLYER_SLOTS.map((slot) => {
                        const options = flyerPickOptions(sections, slot);
                        const current = flyerPicks[slot] ? JSON.stringify(flyerPicks[slot]) : '';
                        const known = options.some((o) => o.value === current);
                        return (
                          <div key={slot} className="grid grid-cols-[minmax(0,11rem)_1fr] gap-2 items-center">
                            <span className="text-xs text-gray-600">{FLYER_SLOT_INFO[slot].label}</span>
                            <Select
                              aria-label={`Flyer ${FLYER_SLOT_INFO[slot].label}`}
                              value={current}
                              onChange={(e) => {
                                const v = e.target.value;
                                setFlyerPicks((prev) => {
                                  const next = { ...prev };
                                  if (!v) delete next[slot]; else next[slot] = JSON.parse(v);
                                  return next;
                                });
                              }}
                              options={[
                                { value: '', label: 'Automatic - by card title' },
                                ...options.map((o) => ({ value: o.value, label: o.label })),
                                ...(current && !known ? [{ value: current, label: `${flyerPicks[slot]!.section} · ${flyerPicks[slot]!.block || '(stat cards)'} - no longer on a page` }] : []),
                              ]}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
                <div>
                  <label className={label}>Sale or lease</label>
                  <Select
                    value={dealKind}
                    onChange={(e) => setDealKind(e.target.value as 'sale' | 'lease')}
                    options={DEAL_KINDS}
                  />
                </div>
                <div>
                  {/* ⭐ Its own choice, not a consequence of sale/lease: four of five lease
                      brochures carry the house wording (measured 2026-09-08). */}
                  <label className={label}>Closing bands</label>
                  <Select
                    value={bandSet}
                    onChange={(e) => setBandSet(e.target.value as 'house' | 'leasing')}
                    options={BAND_SETS}
                  />
                </div>
                <div className="col-span-2 flex flex-wrap items-end gap-3 text-sm">
                  <label className="flex items-center gap-1">
                    <input type="checkbox" checked={includeRobert} onChange={(e) => setIncludeRobert(e.target.checked)} />
                    Robert
                  </label>
                  <label className="flex items-center gap-1">
                    <input type="checkbox" checked={includeJoseph} onChange={(e) => setIncludeJoseph(e.target.checked)} />
                    Joseph
                  </label>
                  <label className="flex items-center gap-1" title="Name, title, phone, email and DRE under PRESENTED BY: - the 2255 / 695 cover">
                    <input type="checkbox" checked={brokerTitles} onChange={(e) => setBrokerTitles(e.target.checked)} />
                    Broker titles (newer covers)
                  </label>
                </div>
              </div>

              <details open className="border rounded-lg p-3">
                <summary className="text-sm font-semibold cursor-pointer">Cover and back cover</summary>
                <div className="mt-2 space-y-2">
                  <div>
                    <label className={label}>Cover style</label>
                    <Select
                      value={coverStyle}
                      onChange={(e) => setCoverStyle(e.target.value as 'card' | 'overlay' | 'band')}
                      options={COVER_STYLES}
                    />
                  </div>
                  <div>
                    <label className={label}>Red banner across the top</label>
                    <Input value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder="PRIME DOWNTOWN HEMET COMMERCIAL LAND  •  ±0.96 AC" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className={label}>Big red line</label><Input value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="608 & 614" /></div>
                    <div><label className={label}>Street line</label><Input value={subhead} onChange={(e) => setSubhead(e.target.value)} placeholder="N. SAN JACINTO STREET" /></div>
                    <div className="col-span-2"><label className={label}>Emphasis line (Apex cover)</label><Input value={emphasis} onChange={(e) => setEmphasis(e.target.value)} placeholder="Retail Anchor Space" /></div>
                  </div>
                  <div>
                    <label className={label}>Stat strip over the photo (2255 / 695 covers) - one "value | label" per line</label>
                    <textarea
                      className={ta}
                      rows={3}
                      value={coverStats}
                      onChange={(e) => setCoverStats(e.target.value)}
                      placeholder={'±15,000 | SF AVAILABLE\n$0.85 | /SF/MO NNN\nC-1 | COMMERCIAL ZONING'}
                      aria-label="Cover stat strip"
                    />
                  </div>
                  <div><label className={label}>Byline under the address</label><Input value={byline} onChange={(e) => setByline(e.target.value)} placeholder="FAMILY DOLLAR · DEAL HUNTERZ · MULTI-TENANT RETAIL CENTER" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className={label}>Back cover band</label><Input value={backBand} onChange={(e) => setBackBand(e.target.value)} placeholder="defaults to the address" /></div>
                    <div><label className={label}>Running footer tag</label><Input value={footerTag} onChange={(e) => setFooterTag(e.target.value)} placeholder="FOR LEASE" /></div>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs">
                    <label className="flex items-center gap-1">
                      <input type="checkbox" checked={pageFooter} onChange={(e) => setPageFooter(e.target.checked)} />
                      Running page footer ("02 · ADDRESS · CITY · FOR LEASE")
                    </label>
                    <label className="flex items-center gap-1">
                      <input type="checkbox" checked={omitBack} onChange={(e) => setOmitBack(e.target.checked)} />
                      No back cover (end on the contact page)
                    </label>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div><label className={label}>Cover photo</label><input type="file" accept="image/*" onChange={(e) => pickPhoto(e, setCoverPhoto)} /></div>
                    <div><label className={label}>Back photo</label><input type="file" accept="image/*" onChange={(e) => pickPhoto(e, setBackPhoto)} /></div>
                    <div><label className={label}>QR code</label><input type="file" accept="image/*" onChange={(e) => pickPhoto(e, setQrSrc)} /></div>
                  </div>
                  {/* 🚨 The record's only photograph for 1,273 properties is a Google STREET VIEW
                      capture, stored as an "exterior" and carrying a "© Google" watermark burned
                      into the pixels. It went onto a client cover unnoticed because grep cannot
                      see a watermark - only printing the page did. Reported, never withheld: the
                      photo is still there and one click replaces it. */}
                  {prefill?.photos?.heroIsStreetView && coverPhoto === prefill?.photos?.hero?.src && (
                    <div className="text-xs text-warning bg-warning-wash border border-warning/30 rounded p-2">
                      The cover photo is a Google Street View capture - it carries a "© Google"
                      watermark and is shot from the road, not of the building. Fine for a working
                      draft; upload a photograph before this goes to a client.
                    </div>
                  )}
                  <div className="text-xs">
                    <label className={label}>Photo grid ({gridPhotos.length} selected)</label>
                    <input type="file" accept="image/*" multiple onChange={pickGrid} />
                  </div>
                </div>
              </details>

              <details className="border rounded-lg p-3">
                <summary className="text-sm font-semibold cursor-pointer">Contact page and standing text</summary>
                <div className="mt-2 space-y-2">
                  <div>
                    <label className={label}>Contact page lead</label>
                    <Select value={contactLead} onChange={(e) => setContactLead(e.target.value)} options={CONTACT_LEADS} />
                  </div>
                  <div><label className={label}>Note under the QR code</label><Input value={contactNote} onChange={(e) => setContactNote(e.target.value)} placeholder="Scan for photos, site plan & brochure: apex-res.com/..." /></div>
                  <div>
                    <label className={label}>Disclaimer (leave empty for the standing Apex text)</label>
                    <textarea className={ta} rows={3} value={disclaimerText} onChange={(e) => setDisclaimerText(e.target.value)} aria-label="Disclaimer" />
                  </div>
                </div>
              </details>

              <details className="border rounded-lg p-3" open>
                <summary className="text-sm font-semibold cursor-pointer">
                  Draft the persuasion with AI
                </summary>
                <div className="mt-3 space-y-3">
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    AI drafts the persuasion, never the facts. It only sees what is on this form,
                    and any line asserting a figure, a freeway, a business name or a zoning
                    permission the form does not carry is dropped and listed below rather than
                    printed. Read every line before you send it.
                  </p>

                  <div className="flex flex-wrap gap-3 text-xs">
                    {DRAFT_OPTIONS.map((o) => (
                      <label key={o.value} className="flex items-center gap-1.5">
                        <input
                          type="checkbox"
                          checked={draftKinds.includes(o.value)}
                          onChange={(e) =>
                            setDraftKinds((prev) =>
                              e.target.checked
                                ? [...prev, o.value]
                                : prev.filter((k) => k !== o.value)
                            )
                          }
                        />
                        <span>{o.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* ⛔ SEPARATED FROM THE LIST ABOVE ON PURPOSE, not styled apart for looks.
                      Everything above is drafted BY A MODEL. This one is assembled from the
                      signed municipal rule book with a citation, and never passes through a
                      model at all - a permitted-use list is a fact. It is a different KIND of
                      thing, so it does not sit in the same row pretending otherwise. */}
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-2">
                    <label className={`flex items-start gap-1.5 text-xs ${zoningCode ? '' : 'opacity-60'}`}>
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={wantZoningPage && !!zoningCode}
                        disabled={!zoningCode}
                        onChange={(e) => setWantZoningPage(e.target.checked)}
                      />
                      <span>
                        <span className="font-medium">What can be built here</span>
                        {zoningCode ? (
                          <span className="text-gray-500">
                            {' '}- built from the signed zoning rule book for {city || 'this city'}{' '}
                            {zoningCode}, with the code cited. Not written by AI.
                          </span>
                        ) : (
                          <span className="text-gray-500">
                            {' '}- add a "Zoning:" row to the property summary and this can be built
                            from the rule book.
                          </span>
                        )}
                      </span>
                    </label>
                  </div>

                  <div>
                    <label className={label}>Anything you want it to lean on (optional)</label>
                    <Input
                      value={guidance}
                      onChange={(e) => setGuidance(e.target.value)}
                      placeholder="e.g. lean on the assemblage angle, aim at owner-users"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={draft}
                      disabled={drafting || !input || (!draftKinds.length && !(wantZoningPage && zoningCode))}
                    >
                      {drafting ? 'Drafting...' : 'Draft with AI'}
                    </Button>
                    {draftNote && <span className="text-xs text-gray-600 dark:text-gray-400">{draftNote}</span>}
                  </div>

                  {dropped.length > 0 && (
                    /* ⭐ REPORTED, NEVER SILENT. A dropped line is usually something Joseph knows
                       and the record does not - the freeway two blocks away, the hospital across
                       the street. This list is a to-do for him, not an apology from the software:
                       every one of these is a line he can simply type. */
                    <div className="rounded-md border border-warning/30 bg-warning-wash p-2 dark:bg-warning/30">
                      <div className="text-xs font-semibold">
                        {dropped.length} line{dropped.length === 1 ? '' : 's'} dropped - the form does
                        not carry what they claim. Type them yourself if you know they are true.
                      </div>
                      <ul className="mt-1 space-y-1">
                        {dropped.map((d, i) => (
                          <li key={i} className="text-xs">
                            <span className="italic">"{d.line}"</span>{' '}
                            <span className="text-warning dark:text-warning-wash">
                              - unsupported: {d.claims.map((c) => `${c.claim} (${c.kind})`).join(', ')}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {zoningRefusal && (
                    /* The zoning page was asked for and could not be built. ⭐ Said out loud
                       rather than silently omitted - and the drafts he asked for beside it were
                       delivered anyway, which is why this is a note and not an error screen. */
                    <div className="rounded-md border border-warning/30 bg-warning-wash p-2 dark:bg-warning/30">
                      <div className="text-xs font-semibold">No zoning page</div>
                      <p className="mt-1 text-xs">{zoningRefusal}</p>
                    </div>
                  )}

                  {withheld.length > 0 && (
                    /* ⛔ The uses the rule book would NOT state on a client document, and the
                       reason for each. A held rule shows its QUESTION here, never its provisional
                       answer - all 82 held rules are by-right-with-a-caveat, so they are exactly
                       the ones that could wrongly tell an owner yes. Each reason names an action. */
                    <div className="rounded-md border border-info/30 bg-info-wash p-2">
                      <div className="text-xs font-semibold">
                        {withheld.length} use{withheld.length === 1 ? '' : 's'} left off the zoning
                        page - the rule book will not state {withheld.length === 1 ? 'it' : 'them'} yet.
                      </div>
                      <ul className="mt-1 space-y-1">
                        {withheld.map((w, i) => (
                          <li key={i} className="text-xs">
                            <span className="font-medium">{w.use}</span>{' '}
                            <span className="text-info">- {w.reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </details>

              {sections.map((s) => (
                <div key={s.id} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Input value={s.title} onChange={(e) => patchSection(s.id, { title: e.target.value })} placeholder="SECTION TITLE" className="font-semibold" />
                    <Button variant="ghost" size="sm" onClick={() => setSections((p) => p.filter((x) => x.id !== s.id))}>Remove</Button>
                  </div>
                  <Input value={s.subtitle} onChange={(e) => patchSection(s.id, { subtitle: e.target.value })} placeholder="italic subtitle (optional)" />
                  <Input value={s.sourceNote} onChange={(e) => patchSection(s.id, { sourceNote: e.target.value })} placeholder="Source: ... (optional)" />
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                    <label className="flex items-center gap-1">
                      <input type="checkbox" checked={s.omitDisclaimer} onChange={(e) => patchSection(s.id, { omitDisclaimer: e.target.checked })} />
                      No disclaimer on this page (his photo page has none)
                    </label>
                    <label className="flex items-center gap-1">
                      <input type="checkbox" checked={s.omitFooter} onChange={(e) => patchSection(s.id, { omitFooter: e.target.checked })} />
                      No running footer on this page
                    </label>
                    <label className="flex items-center gap-1">
                      Columns
                      <Select
                        value={s.columns}
                        onChange={(e) => patchSection(s.id, { columns: e.target.value as '2' | '3' })}
                        className="text-xs w-16"
                        options={[{ value: '2', label: '2' }, { value: '3', label: '3' }]}
                      />
                    </label>
                  </div>

                  {s.blocks.map((b) => (
                    <div key={b.id} className="border-l-2 border-gray-200 pl-2 space-y-1">
                      <div className="flex items-center gap-1">
                        <Select
                          value={b.kind}
                          onChange={(e) => patchBlock(s.id, b.id, { kind: e.target.value as BlockKind })}
                          className="text-xs"
                          options={KINDS}
                        />
                        <Select
                          value={b.column}
                          onChange={(e) => patchBlock(s.id, b.id, { column: e.target.value as '' | '1' | '2' })}
                          className="text-xs w-28"
                          options={COLUMNS}
                        />
                        <Button variant="ghost" size="sm" onClick={() => patchBlock(s.id, b.id, { full: !b.full })}>
                          {b.full ? 'full width' : 'half'}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => patchSection(s.id, { blocks: s.blocks.filter((x) => x.id !== b.id) })}>x</Button>
                      </div>
                      <Input value={b.title} onChange={(e) => patchBlock(s.id, b.id, { title: e.target.value })} placeholder="CARD TITLE" className="text-sm" />
                      <textarea
                        className={ta}
                        rows={b.kind === 'prose' ? 5 : 4}
                        value={b.body}
                        onChange={(e) => patchBlock(s.id, b.id, { body: e.target.value })}
                        placeholder={KIND_HELP[b.kind]}
                      />
                      {pairFormatProblem(b.kind, b.body) ? (
                        <p className="text-[11px] text-warning bg-warning-wash border border-warning/30 rounded px-2 py-1">
                          {pairFormatProblem(b.kind, b.body)}
                        </p>
                      ) : (
                        <p className="text-[11px] text-gray-500">{KIND_HELP[b.kind]}</p>
                      )}
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => patchSection(s.id, { blocks: [...s.blocks, newBlock()] })}>
                    Add block
                  </Button>
                </div>
              ))}
              <Button variant="outline" onClick={() => setSections((p) => [...p, newSection()])}>Add page</Button>
            </div>

            {/* ---- the live preview: the server's own HTML ---- */}
            <div className="flex flex-col min-h-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Button onClick={print} disabled={!preview}>Print / PDF</Button>
                <Button variant="outline" onClick={download} disabled={!preview}>Download HTML</Button>
                <Button variant="outline" onClick={save} disabled={!preview || busy}>
                  {busy ? (dealId ? 'Filing...' : 'Saving...') : dealId ? 'File on this deal' : 'Save brochure'}
                </Button>
              </div>

              {filed && <p className="text-xs text-success mb-1">{filed}</p>}

              {overflowing.length > 0 && (
                <div className="mb-2 p-2 rounded bg-warning-wash border border-warning/30 text-xs text-warning">
                  Page {overflowing.join(', ')} has more content than fits. A brochure page is a
                  fixed box, so the overflow will be CUT OFF when it prints - shorten it or move
                  some of it to another page.
                </div>
              )}

              {preview && preview.missing.length > 0 && (
                <div className="mb-2 p-2 rounded bg-gray-50 border text-xs text-gray-600">
                  Not supplied yet: {preview.missing.join(', ')}. Nothing is invented for these -
                  they simply do not print.
                </div>
              )}

              <div className="flex-1 border rounded-lg overflow-hidden bg-gray-200">
                <iframe
                  ref={frame}
                  title="Brochure preview"
                  className="w-full h-full"
                  style={{ border: 0 }}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
